import {
  type Carer,
  type Resident,
  type ResidentItem,
  ResidentStatus,
  type User
} from '@lmc/ts-common'
import { Avatar } from '@lmc/ui-kit'
import useUsersList from 'hooks/useUser/useUsersList'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useCarers } from 'screens/Organisation/screens/TeamList'

const getName = (item: { name?: { first?: string; last?: string } }) =>
  `${item?.name?.first} ${item?.name?.last}`

export type CarerEmailWithName = { email: string; name: string }

export const useGroupedResidents = (residents: (ResidentItem | Resident)[]) => {
  const groupedResidents: { [status: string]: Resident[] } = {
    [ResidentStatus.active]: [],
    [ResidentStatus.inactive]: [],
    [ResidentStatus.hospitalised]: [],
    [ResidentStatus.deceased]: [],
    [ResidentStatus.discharged]: []
  }

  residents?.forEach((resident) => {
    const { status } = resident as unknown as Resident
    if (!groupedResidents[status]) {
      groupedResidents[status] = []
    }
    groupedResidents[status].push(resident as unknown as Resident)
  })

  Object.keys(groupedResidents).forEach((key) => {
    if (groupedResidents[key].length === 0) {
      delete groupedResidents[key]
    } else {
      groupedResidents[key].sort((a, b) => {
        const nameA = getName(a)
        const nameB = getName(b)
        return nameA.localeCompare(nameB)
      })
    }
  })

  const groupedResidentOptions = Object.keys(groupedResidents).map(
    (status) => ({
      label: status.toLocaleUpperCase(),
      options: groupedResidents[status].map((resident) => ({
        label: getName(resident),
        value: resident.id,
        image: <Avatar url={resident.picture as unknown as string} size={20} />
      }))
    })
  )

  return { groupedResidentOptions }
}

export const useGroupedCarers = <K extends keyof Carer = 'id'>(
  excludeGroupUsers?: boolean,
  valueKey?: K
) => {
  const key = (valueKey ?? 'id') as K
  const { carers } = useCarers(excludeGroupUsers)

  const groupedCarerOptions = useMemo(() => {
    const grouped: { [status: string]: Carer[] } = {
      active: [],
      inactive: []
    }

    carers
      ?.filter(
        (carer) =>
          !carer?.deleted &&
          !(carer as unknown as Carer & { hidden?: boolean })?.hidden &&
          carer[key]
      )
      .forEach((carer) => {
        if (carer.active) grouped.active.push(carer as unknown as Carer)
        else grouped.inactive.push(carer as unknown as Carer)
      })

    Object.keys(grouped).forEach((groupKey) => {
      if (grouped[groupKey].length === 0) delete grouped[groupKey]
      else
        grouped[groupKey].sort((a, b) => getName(a).localeCompare(getName(b)))
    })

    return Object.keys(grouped).map((status) => ({
      label: status.toLocaleUpperCase(),
      options: grouped[status].map((carer) => ({
        label: getName(carer),
        value: carer[key] as string,
        image: <Avatar url={carer.picture as unknown as string} size={20} />
      }))
    }))
  }, [carers, key])

  return { groupedCarerOptions }
}

export const useGroupedUsers = () => {
  const { users } = useUsersList({})
  const { t } = useTranslation()

  const groupedUsers: { [status: string]: User[] } = {
    [t('ACTIVE')]: [],
    [t('INACTIVE')]: []
  }

  users?.forEach((user: User) => {
    if (user.active) {
      groupedUsers[t('ACTIVE')].push(user)
    } else {
      groupedUsers[t('INACTIVE')].push(user)
    }
  })

  Object.keys(groupedUsers).forEach((key) => {
    if (groupedUsers[key].length === 0) {
      delete groupedUsers[key]
    } else {
      groupedUsers[key].sort((a, b) => {
        const nameA = getName(a)
        const nameB = getName(b)
        return nameA.localeCompare(nameB)
      })
    }
  })

  const groupedUserOptions =
    Object.keys(groupedUsers).map((status) => ({
      label: status.toLocaleUpperCase(),
      options: groupedUsers[status].map((user) => ({
        label: getName(user),
        value: user.id,
        image: <Avatar url={user.picture as unknown as string} size={20} />
      }))
    })) || []

  return { groupedUserOptions }
}

export const useGroupedCarersWithSelected = ({
  assignedUserId,
  skip = false
}: {
  assignedUserId?: string | null
  skip?: boolean
}) => {
  const carers = useSelector(
    (state: { data: { carers: Carer[] } }) => state.data.carers
  )

  if (skip) return []

  const activeCarers = carers
    ?.filter((carer) => carer.active)
    .map((carer) => ({
      label: `${carer.name.first} ${carer.name.last}`,
      value: carer.id
    }))

  const inactiveCarers = assignedUserId
    ? carers
        ?.filter(
          (carer) =>
            carer.active === false && carer.id.toString() === assignedUserId
        )
        .map((carer) => ({
          label: `${carer.name.first} ${carer.name.last}`,
          value: carer.id
        }))
    : []

  const deletedCarers = assignedUserId
    ? carers
        ?.filter(
          (carer) => carer.deleted && carer.id.toString() === assignedUserId
        )
        .map((carer) => ({
          label: `${carer.name.first} ${carer.name.last}`,
          value: carer.id
        }))
    : []

  const groupedCarerOptions = [
    {
      label: 'ACTIVE',
      options: activeCarers
    },
    ...(inactiveCarers.length
      ? [
          {
            label: 'INACTIVE',
            options: inactiveCarers
          }
        ]
      : []),
    ...(deletedCarers.length
      ? [
          {
            label: 'DELETED',
            options: deletedCarers
          }
        ]
      : [])
  ]

  return groupedCarerOptions
}
